import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { useCompany } from '../../Contexts/CompanyContext';
import {
  OrderReturnService,
  ReturnReason,
  ReturnItem,
  ReturnRequest,
  RETURN_REASON_LABELS,
  DAMAGE_REASONS,
} from '../../Services/orderReturnService';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Types                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

interface OrderItemRow {
  productId: string;
  productName: string;
  productSku?: string;
  productImage?: string;
  variantId?: string;
  variantSelections?: Array<{ variantType: string; variantValue: string }>;
  quantity: number;           // original quantity on the order
  unitPrice: number;          // original list price (before discount)
  discountedUnitPrice?: number; // actual price charged (after discount) — stored by checkoutService
  discountPercentage?: number;  // discount % applied
  totalPrice: number;         // discountedUnitPrice * quantity (what customer paid)
  sourceWarehouseId?: string;
  sourceWarehouseName?: string;
}

interface FoundOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  status: string;
  totalAmount: number;
  subtotal?: number;
  discountAmount?: number;  // order-level discount
  orderDate: Date;
  items: OrderItemRow[];
  isWarehouseAllocated: boolean;
  paymentStatus: string;
  paymentMethod: string;
}

interface SelectedItem extends ReturnItem {
  selectedQty: number;          // how many to return (≤ original quantity)
  discountedUnitPrice: number;  // actual price per unit (what was charged)
  discountPercentage: number;   // for display
  originalUnitPrice: number;    // list price before discount (for display)
}

type Step = 'search' | 'select-items' | 'reason' | 'review' | 'success';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

const variantLabel = (item: OrderItemRow): string => {
  if (!item.variantSelections || item.variantSelections.length === 0) return '';
  return item.variantSelections.map(s => `${s.variantType}: ${s.variantValue}`).join(', ');
};

/**
 * Resolve the effective (discounted) unit price for an order item.
 *
 * The Firestore order document stores the discount ONLY at the order level
 * (discountAmount on the order, NOT on individual items). Item fields
 * unitPrice and totalPrice are both the full list price — undiscounted.
 *
 * Strategy:
 *  1. If item.discountedUnitPrice is explicitly stored and < unitPrice → use it.
 *  2. If item.discountPercentage is stored and > 0 → derive from unitPrice.
 *  3. Distribute order-level discountAmount proportionally by each item's share
 *     of the subtotal:
 *       itemDiscount = discountAmount × (itemSubtotal / orderSubtotal)
 *       effectiveUnitPrice = unitPrice − (itemDiscount / qty)
 *  4. Fallback: unitPrice (no discount).
 */
const resolveEffectiveUnitPrice = (
  item: OrderItemRow,
  orderDiscountAmount?: number,
  orderSubtotal?: number
): number => {
  // ── 1. Explicitly stored discounted price (lower than list price) ──
  if (
    item.discountedUnitPrice !== undefined &&
    item.discountedUnitPrice > 0 &&
    item.discountedUnitPrice < item.unitPrice
  ) {
    return item.discountedUnitPrice;
  }

  // ── 2. Item-level discount percentage ──
  if (item.discountPercentage && item.discountPercentage > 0 && item.discountPercentage <= 100) {
    return item.unitPrice * (1 - item.discountPercentage / 100);
  }

  // ── 3. Distribute order-level discountAmount proportionally ──
  //    This is the main case: discount is only stored on the order document.
  if (
    orderDiscountAmount &&
    orderDiscountAmount > 0 &&
    orderSubtotal &&
    orderSubtotal > 0 &&
    item.unitPrice > 0 &&
    item.quantity > 0
  ) {
    const itemSubtotal = item.unitPrice * item.quantity;               // e.g. 1000
    const itemDiscountShare = orderDiscountAmount * (itemSubtotal / orderSubtotal); // e.g. 200 * (1000/1000) = 200
    const itemDiscountPerUnit = itemDiscountShare / item.quantity;     // e.g. 200 / 1 = 200
    return item.unitPrice - itemDiscountPerUnit;                       // e.g. 1000 - 200 = 800
  }

  // ── 4. Fallback: no discount ──
  return item.unitPrice;
};

/**
 * Compute the effective discount percentage for display.
 * Returns 0 if no meaningful discount (< 0.5%).
 */
const resolveDiscountPct = (original: number, effective: number): number => {
  if (!original || original <= 0) return 0;
  const pct = ((original - effective) / original) * 100;
  return pct > 0.5 ? Math.round(pct) : 0;
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Component                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

const OrderReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { formatPrice } = useCompany();

  /* ── Step state ── */
  const [step, setStep] = useState<Step>('search');

  /* ── Search form ── */
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  /* ── Found order ── */
  const [order, setOrder] = useState<FoundOrder | null>(null);
  const [eligibilityError, setEligibilityError] = useState('');
  const [existingReturn, setExistingReturn] = useState<ReturnRequest | null>(null);

  /* ── Item selection ── */
  const [selectedItems, setSelectedItems] = useState<Record<string, SelectedItem>>({});

  /* ── Return reason form ── */
  const [reason, setReason] = useState<ReturnReason | ''>('');
  const [damageNotes, setDamageNotes] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  /* ── Submission ── */
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [returnRequestId, setReturnRequestId] = useState('');

  /* ── Pre-fill from query string (?orderId=...) ── */
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (orderId) {
      (async () => {
        try {
          const found = await OrderReturnService.getOrderById(orderId);
          if (found) {
            setOrder(found as FoundOrder);
            setOrderNumber(found.orderNumber);
            setEmail(found.customerEmail);
            checkEligibilityAndExisting(found as FoundOrder);
          }
        } catch {
          // Silently ignore — let the user search manually
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Eligibility + existing return check ── */
  const checkEligibilityAndExisting = async (found: FoundOrder) => {
    const eligibility = OrderReturnService.isEligibleForReturn(found);
    if (!eligibility.eligible) {
      setEligibilityError(eligibility.reason || 'Order is not eligible for return.');
      return;
    }
    setEligibilityError('');

    const existing = await OrderReturnService.getExistingReturn(found.id);
    if (existing) {
      setExistingReturn(existing);
      setStep('success'); // show existing return status
      return;
    }

    setStep('select-items');
  };

  /* ── Search ── */
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;

    setSearching(true);
    setSearchError('');
    setEligibilityError('');

    try {
      const found = await OrderReturnService.findOrder(orderNumber.trim(), email.trim().toLowerCase());
      if (!found) {
        setSearchError('No order found. Please check your order number and email address.');
        setSearching(false);
        return;
      }

      setOrder(found as FoundOrder);
      await checkEligibilityAndExisting(found as FoundOrder);
    } catch (err) {
      setSearchError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  /* ── Item selection ── */
  const handleToggleItem = (item: OrderItemRow) => {
    const key = item.variantId ? `${item.productId}_${item.variantId}` : item.productId;

    setSelectedItems(prev => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }

      // Resolve the actual price the customer paid per unit,
      // passing order-level discount info as fallback
      const effectiveUnitPrice = resolveEffectiveUnitPrice(
        item,
        order?.discountAmount,
        order?.subtotal
      );
      const discountPct = resolveDiscountPct(item.unitPrice, effectiveUnitPrice);

      return {
        ...prev,
        [key]: {
          productId: item.productId,
          productName: item.productName,
          productSku: item.productSku,
          productImage: item.productImage,
          variantId: item.variantId,
          variantSelections: item.variantSelections,
          quantity: item.quantity,
          unitPrice: effectiveUnitPrice,             // used by ReturnItem interface & refund calc
          discountedUnitPrice: effectiveUnitPrice,   // explicit for UI display
          originalUnitPrice: item.unitPrice,         // list price for strikethrough display
          discountPercentage: discountPct,
          totalPrice: effectiveUnitPrice * item.quantity,
          selectedQty: item.quantity,
        },
      };
    });
  };

  const handleQtyChange = (key: string, qty: number, maxQty: number) => {
    const clamped = Math.max(1, Math.min(maxQty, qty));
    setSelectedItems(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        selectedQty: clamped,
        totalPrice: prev[key].discountedUnitPrice * clamped,
      },
    }));
  };

  const selectedList = Object.values(selectedItems);

  // Build the list we pass to calculateRefund — quantity = selectedQty, totalPrice = discountedUnitPrice * selectedQty
  const refundItems: ReturnItem[] = selectedList.map(i => ({
    productId: i.productId,
    productName: i.productName,
    productSku: i.productSku,
    productImage: i.productImage,
    variantId: i.variantId,
    variantSelections: i.variantSelections,
    quantity: i.selectedQty,
    unitPrice: i.discountedUnitPrice,
    totalPrice: i.discountedUnitPrice * i.selectedQty,
  }));

  const refundAmount = OrderReturnService.calculateRefund(refundItems);

  /* ── Step navigation ── */
  const goToReason = () => {
    if (selectedList.length === 0) return;
    setStep('reason');
  };

  const goToReview = () => {
    if (!reason) return;
    if (DAMAGE_REASONS.includes(reason as ReturnReason) && !damageNotes.trim()) return;
    setStep('review');
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!order || !reason) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const returnItems: ReturnItem[] = selectedList.map(i => ({
        productId: i.productId,
        productName: i.productName,
        productSku: i.productSku,
        productImage: i.productImage,
        variantId: i.variantId,
        variantSelections: i.variantSelections,
        quantity: i.selectedQty,
        unitPrice: i.discountedUnitPrice,                    // actual price charged
        totalPrice: i.discountedUnitPrice * i.selectedQty,   // refund for this line
      }));

      const id = await OrderReturnService.submitReturnRequest({
        orderId: order.id,
        orderNumber: order.orderNumber,
        customerName: order.customerName,
        customerEmail: order.customerEmail,
        items: returnItems,
        reason: reason as ReturnReason,
        damageNotes: damageNotes || undefined,
        additionalNotes: additionalNotes || undefined,
        refundAmount,
      });

      setReturnRequestId(id);
      setExistingReturn(null); // clear so success shows the NEW return
      setStep('success');
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit return request. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  /* ─────────────────── RENDER HELPERS ─────────────────── */

  const renderStepIndicator = () => {
    const steps: { key: Step; label: string }[] = [
      { key: 'search', label: 'Find Order' },
      { key: 'select-items', label: 'Select Items' },
      { key: 'reason', label: 'Return Reason' },
      { key: 'review', label: 'Review' },
    ];
    const currentIdx = steps.findIndex(s => s.key === step);
    if (step === 'success') return null;

    return (
      <div className="return-steps">
        {steps.map((s, idx) => (
          <React.Fragment key={s.key}>
            <div className={`return-step ${idx <= currentIdx ? 'return-step--active' : ''} ${idx < currentIdx ? 'return-step--done' : ''}`}>
              <div className="return-step__bubble">
                {idx < currentIdx
                  ? <i className="zmdi zmdi-check"></i>
                  : <span>{idx + 1}</span>
                }
              </div>
              <span className="return-step__label">{s.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`return-step__line ${idx < currentIdx ? 'return-step__line--done' : ''}`}></div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  /* ── STEP: SEARCH ── */
  const renderSearch = () => (
    <div className="return-card">
      <div className="return-card__header">
        <i className="zmdi zmdi-refresh-alt return-card__icon"></i>
        <div>
          <h4 className="return-card__title">Start a Return</h4>
          <p className="return-card__sub">Enter your order number and email to get started.</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="return-form">
        <div className="return-form__row">
          <div className="return-form__field">
            <label className="return-form__label">Order Number</label>
            <div className="return-input-wrap">
              <i className="zmdi zmdi-receipt return-input-wrap__icon"></i>
              <input
                className="return-input stext-111 cl8"
                type="text"
                placeholder="e.g. ORD-1718023456-A1B"
                value={orderNumber}
                onChange={e => setOrderNumber(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="return-form__field">
            <label className="return-form__label">Email Address</label>
            <div className="return-input-wrap">
              <i className="zmdi zmdi-email return-input-wrap__icon"></i>
              <input
                className="return-input stext-111 cl8"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {searchError && (
          <div className="return-alert return-alert--error">
            <i className="zmdi zmdi-alert-circle-o"></i>
            {searchError}
          </div>
        )}

        {eligibilityError && (
          <div className="return-alert return-alert--warning">
            <i className="zmdi zmdi-info-outline"></i>
            {eligibilityError}
          </div>
        )}

        <button
          type="submit"
          disabled={searching || !orderNumber.trim() || !email.trim()}
          className="return-btn return-btn--primary"
        >
          {searching
            ? <><i className="zmdi zmdi-refresh return-spin m-r-8"></i>Searching…</>
            : <><i className="zmdi zmdi-search m-r-8"></i>Find My Order</>
          }
        </button>
      </form>

      <div className="return-policy-note">
        <i className="zmdi zmdi-info-outline m-r-5"></i>
        Returns are accepted within <strong>30 days</strong> of delivery for <strong>delivered</strong> orders only.
      </div>
    </div>
  );

  /* ── STEP: SELECT ITEMS ── */
  const renderSelectItems = () => {
    if (!order) return null;
    return (
      <div>
        <div className="return-order-badge">
          <span className="stext-111 cl6">Order <strong>{order.orderNumber}</strong></span>
          <span className="stext-111 cl6">
            Placed {order.orderDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>

        <div className="return-card">
          <div className="return-card__header">
            <i className="zmdi zmdi-shopping-basket return-card__icon"></i>
            <div>
              <h4 className="return-card__title">Select Items to Return</h4>
              <p className="return-card__sub">Choose the items you want to return and the quantity.</p>
            </div>
          </div>

          <div className="return-items-list">
            {order.items.map((item, idx) => {
              const key = item.variantId ? `${item.productId}_${item.variantId}` : item.productId;
              const isSelected = !!selectedItems[key];
              const sel = selectedItems[key];
              const vLabel = variantLabel(item);

              // Resolve effective price passing order-level discount as fallback
              const effectiveUnitPrice = resolveEffectiveUnitPrice(
                item,
                order.discountAmount,
                order.subtotal
              );
              const computedDiscountPct = resolveDiscountPct(item.unitPrice, effectiveUnitPrice);
              const hasItemDiscount = computedDiscountPct > 0;

              return (
                <div
                  key={idx}
                  className={`return-item ${isSelected ? 'return-item--selected' : ''}`}
                  onClick={() => handleToggleItem(item)}
                >
                  <div className="return-item__check">
                    <div className={`return-checkbox ${isSelected ? 'return-checkbox--checked' : ''}`}>
                      {isSelected && <i className="zmdi zmdi-check"></i>}
                    </div>
                  </div>

                  <div className="return-item__img-wrap">
                    {item.productImage
                      ? <img src={item.productImage} alt={item.productName} className="return-item__img" onError={e => { (e.target as HTMLImageElement).src = '/images/noimage.jpg'; }} />
                      : <div className="return-item__img-placeholder"><i className="zmdi zmdi-image-o"></i></div>
                    }
                  </div>

                  <div className="return-item__info">
                    <p className="return-item__name stext-111 cl2">{item.productName}</p>
                    {vLabel && <p className="return-item__variant stext-113 cl6">{vLabel}</p>}
                    {item.productSku && <p className="return-item__sku stext-113 cl6">SKU: {item.productSku}</p>}

                    {/* Price display: show discounted price with original struck-through if discounted */}
                    {hasItemDiscount ? (
                      <div className="return-item__price-wrap">
                        <span className="return-item__price-discounted stext-111 cl2">
                          {formatPrice(effectiveUnitPrice)}
                        </span>
                        <span className="return-item__price-original stext-113">
                          {formatPrice(item.unitPrice)}
                        </span>
                        <span className="return-item__discount-badge">
                          -{computedDiscountPct}%
                        </span>
                      </div>
                    ) : (
                      <p className="return-item__price stext-111 cl2">{formatPrice(effectiveUnitPrice)} each</p>
                    )}
                  </div>

                  <div className="return-item__qty-wrap" onClick={e => e.stopPropagation()}>
                    {isSelected ? (
                      <div className="return-qty">
                        <label className="return-qty__label">Qty to return</label>
                        <div className="return-qty__controls">
                          <button
                            type="button"
                            className="return-qty__btn"
                            onClick={() => handleQtyChange(key, (sel.selectedQty || 1) - 1, item.quantity)}
                          >−</button>
                          <span className="return-qty__val">{sel.selectedQty}</span>
                          <button
                            type="button"
                            className="return-qty__btn"
                            onClick={() => handleQtyChange(key, (sel.selectedQty || 1) + 1, item.quantity)}
                          >+</button>
                        </div>
                        <span className="return-qty__max stext-113 cl6">of {item.quantity}</span>
                      </div>
                    ) : (
                      <span className="stext-113 cl6">Qty: {item.quantity}</span>
                    )}
                  </div>

                  <div className="return-item__total">
                    <span className="return-item__total-val stext-111 cl2">
                      {formatPrice(
                        isSelected
                          ? sel.discountedUnitPrice * sel.selectedQty
                          : effectiveUnitPrice * item.quantity
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {selectedList.length > 0 && (
            <div className="return-refund-preview">
              <i className="zmdi zmdi-money m-r-8" style={{ color: '#28a745' }}></i>
              Estimated refund for selected items: <strong>{formatPrice(refundAmount)}</strong>
            </div>
          )}

          <div className="return-actions">
            <button type="button" className="return-btn return-btn--ghost" onClick={() => { setOrder(null); setStep('search'); }}>
              <i className="zmdi zmdi-arrow-left m-r-8"></i>Back
            </button>
            <button
              type="button"
              className="return-btn return-btn--primary"
              disabled={selectedList.length === 0}
              onClick={goToReason}
            >
              Continue<i className="zmdi zmdi-arrow-right m-l-8"></i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── STEP: REASON ── */
  const renderReason = () => (
    <div className="return-card">
      <div className="return-card__header">
        <i className="zmdi zmdi-comment-list return-card__icon"></i>
        <div>
          <h4 className="return-card__title">Why are you returning?</h4>
          <p className="return-card__sub">This helps us process your return faster.</p>
        </div>
      </div>

      <div className="return-reason-grid">
        {(Object.entries(RETURN_REASON_LABELS) as [ReturnReason, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`return-reason-chip ${reason === key ? 'return-reason-chip--active' : ''}`}
            onClick={() => setReason(key)}
          >
            <i className={`zmdi ${getReasonIcon(key)} return-reason-chip__icon`}></i>
            {label}
          </button>
        ))}
      </div>

      {/* Damage notes — required for damaged / defective */}
      {reason && DAMAGE_REASONS.includes(reason as ReturnReason) && (
        <div className="return-form__field m-t-20">
          <label className="return-form__label">
            Damage Description <span className="return-required">*</span>
          </label>
          <textarea
            className="return-textarea stext-111 cl8"
            rows={4}
            placeholder="Please describe the damage or defect in detail…"
            value={damageNotes}
            onChange={e => setDamageNotes(e.target.value)}
          />
          <p className="return-hint">Required for damaged/defective items.</p>
        </div>
      )}

      <div className="return-form__field m-t-20">
        <label className="return-form__label">Additional Notes (optional)</label>
        <textarea
          className="return-textarea stext-111 cl8"
          rows={3}
          placeholder="Any other details you'd like us to know…"
          value={additionalNotes}
          onChange={e => setAdditionalNotes(e.target.value)}
        />
      </div>

      <div className="return-actions">
        <button type="button" className="return-btn return-btn--ghost" onClick={() => setStep('select-items')}>
          <i className="zmdi zmdi-arrow-left m-r-8"></i>Back
        </button>
        <button
          type="button"
          className="return-btn return-btn--primary"
          disabled={!reason || (DAMAGE_REASONS.includes(reason as ReturnReason) && !damageNotes.trim())}
          onClick={goToReview}
        >
          Review Return<i className="zmdi zmdi-arrow-right m-l-8"></i>
        </button>
      </div>
    </div>
  );

  /* ── STEP: REVIEW ── */
  const renderReview = () => {
    if (!order) return null;
    return (
      <div className="return-card">
        <div className="return-card__header">
          <i className="zmdi zmdi-check-square-o return-card__icon"></i>
          <div>
            <h4 className="return-card__title">Review Your Return</h4>
            <p className="return-card__sub">Please confirm the details before submitting.</p>
          </div>
        </div>

        {/* Order info */}
        <div className="return-review-section">
          <h5 className="return-review-section__title">Order Details</h5>
          <div className="return-review-row">
            <span className="return-review-row__label stext-110 cl2">Order Number</span>
            <span className="stext-111 cl6">{order.orderNumber}</span>
          </div>
          <div className="return-review-row">
            <span className="return-review-row__label stext-110 cl2">Customer</span>
            <span className="stext-111 cl6">{order.customerName}</span>
          </div>
          <div className="return-review-row">
            <span className="return-review-row__label stext-110 cl2">Email</span>
            <span className="stext-111 cl6">{order.customerEmail}</span>
          </div>
        </div>

        {/* Items */}
        <div className="return-review-section">
          <h5 className="return-review-section__title">Items Being Returned</h5>
          {selectedList.map((item, idx) => (
            <div key={idx} className="return-review-item">
              <div className="return-review-item__info">
                <p className="stext-111 cl2">{item.productName}</p>
                {item.variantSelections && item.variantSelections.length > 0 && (
                  <p className="stext-113 cl6">{item.variantSelections.map(s => s.variantValue).join(', ')}</p>
                )}
                <p className="stext-113 cl6">Qty: {item.selectedQty}</p>
                {/* Show discount info if applicable */}
                {item.discountPercentage > 0 && (
                  <p className="stext-113" style={{ color: '#e74c3c', fontSize: '11px' }}>
                    Discount applied: -{item.discountPercentage}% (refund based on price paid)
                  </p>
                )}
              </div>
              <div className="return-review-item__prices">
                <span className="stext-111 cl2">{formatPrice(item.discountedUnitPrice * item.selectedQty)}</span>
                {item.discountPercentage > 0 && (
                  <span className="return-review-item__original stext-113">
                    was {formatPrice(item.originalUnitPrice * item.selectedQty)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Return reason */}
        <div className="return-review-section">
          <h5 className="return-review-section__title">Return Reason</h5>
          <div className="return-review-row">
            <span className="return-review-row__label stext-110 cl2">Reason</span>
            <span className="stext-111 cl6">{RETURN_REASON_LABELS[reason as ReturnReason]}</span>
          </div>
          {damageNotes && (
            <div className="return-review-row return-review-row--col">
              <span className="return-review-row__label stext-110 cl2">Damage Description</span>
              <p className="stext-111 cl6 m-t-5">{damageNotes}</p>
            </div>
          )}
          {additionalNotes && (
            <div className="return-review-row return-review-row--col">
              <span className="return-review-row__label stext-110 cl2">Additional Notes</span>
              <p className="stext-111 cl6 m-t-5">{additionalNotes}</p>
            </div>
          )}
        </div>

        {/* Refund */}
        <div className="return-refund-summary">
          <div>
            <span className="stext-110 cl2">Estimated Refund</span>
            <p className="stext-113 cl6" style={{ margin: '2px 0 0', fontSize: '11px' }}>
              Based on price paid (after discounts)
            </p>
          </div>
          <span className="return-refund-summary__amount">{formatPrice(refundAmount)}</span>
        </div>

        <div className="return-policy-note m-t-10 m-b-20">
          <i className="zmdi zmdi-info-outline m-r-5"></i>
          Your return request will be reviewed within 1–2 business days. Refunds are processed within 5–10 business days after approval.
        </div>

        {submitError && (
          <div className="return-alert return-alert--error m-b-20">
            <i className="zmdi zmdi-alert-circle-o"></i>
            {submitError}
          </div>
        )}

        <div className="return-actions">
          <button type="button" className="return-btn return-btn--ghost" onClick={() => setStep('reason')}>
            <i className="zmdi zmdi-arrow-left m-r-8"></i>Back
          </button>
          <button
            type="button"
            className="return-btn return-btn--success"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting
              ? <><i className="zmdi zmdi-refresh return-spin m-r-8"></i>Submitting…</>
              : <><i className="zmdi zmdi-check m-r-8"></i>Submit Return Request</>
            }
          </button>
        </div>
      </div>
    );
  };

  /* ── STEP: SUCCESS ── */
  const renderSuccess = () => {
    const display = existingReturn;
    const isExisting = !!display;
    const statusColor: Record<string, string> = {
      pending: '#ffc107',
      approved: '#28a745',
      rejected: '#dc3545',
      completed: '#717fe0',
    };
    const displayStatus = display?.status || 'pending';

    return (
      <div className="return-card return-card--success">
        <div className="return-success-icon">
          {isExisting && display?.status === 'rejected'
            ? <i className="zmdi zmdi-close-circle" style={{ color: '#dc3545' }}></i>
            : <i className="zmdi zmdi-check-circle" style={{ color: '#28a745' }}></i>
          }
        </div>

        <h3 className="mtext-111 cl2 p-b-10 text-center">
          {isExisting ? 'Existing Return Request' : 'Return Request Submitted!'}
        </h3>
        <p className="stext-113 cl6 text-center p-b-20">
          {isExisting
            ? 'You have already submitted a return request for this order.'
            : 'We\'ve received your return request and will review it shortly.'
          }
        </p>

        <div className="return-success-details">
          {order && (
            <div className="return-review-row">
              <span className="return-review-row__label stext-110 cl2">Order</span>
              <span className="stext-111 cl6">{order.orderNumber}</span>
            </div>
          )}
          {(returnRequestId || display?.id) && (
            <div className="return-review-row">
              <span className="return-review-row__label stext-110 cl2">Return ID</span>
              <span className="stext-111 cl6">{returnRequestId || display?.id}</span>
            </div>
          )}
          <div className="return-review-row">
            <span className="return-review-row__label stext-110 cl2">Status</span>
            <span
              className="stext-111"
              style={{
                backgroundColor: statusColor[displayStatus] || '#ffc107',
                color: displayStatus === 'pending' ? '#000' : '#fff',
                padding: '3px 12px',
                borderRadius: '4px',
                textTransform: 'capitalize',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {displayStatus.replace('_', ' ')}
            </span>
          </div>
          {display?.reason && (
            <div className="return-review-row">
              <span className="return-review-row__label stext-110 cl2">Reason</span>
              <span className="stext-111 cl6">{RETURN_REASON_LABELS[display.reason]}</span>
            </div>
          )}
          {display?.refundAmount !== undefined && display.refundAmount > 0 && (
            <div className="return-review-row">
              <span className="return-review-row__label stext-110 cl2">Refund Amount</span>
              <span className="stext-111 cl6">{formatPrice(display.refundAmount)}</span>
            </div>
          )}
          {display?.adminNotes && (
            <div className="return-review-row return-review-row--col">
              <span className="return-review-row__label stext-110 cl2">Admin Notes</span>
              <p className="stext-111 cl6 m-t-5">{display.adminNotes}</p>
            </div>
          )}
        </div>

        {!isExisting && (
          <div className="return-policy-note m-t-20 m-b-20">
            <i className="zmdi zmdi-info-outline m-r-5"></i>
            Our team will review your request within <strong>1–2 business days</strong>. Refunds typically process within <strong>5–10 business days</strong> after approval.
          </div>
        )}

        <div className="return-actions return-actions--center">
          <a href="/" className="return-btn return-btn--primary">
            <i className="zmdi zmdi-store m-r-8"></i>Continue Shopping
          </a>
          {order && (
            <a href={`/order-tracking?orderId=${order.id}`} className="return-btn return-btn--ghost">
              <i className="zmdi zmdi-truck m-r-8"></i>Track Order
            </a>
          )}
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────────────────────────────────── */
  /*  Icon helper                                                                  */
  /* ─────────────────────────────────────────────────────────────────────────── */

  function getReasonIcon(r: ReturnReason): string {
    const map: Record<ReturnReason, string> = {
      wrong_item: 'zmdi-swap',
      defective: 'zmdi-alert-triangle',
      damaged: 'zmdi-shield-security',
      not_as_described: 'zmdi-info',
      size_issue: 'zmdi-arrows',
      changed_mind: 'zmdi-mood-bad',
      late_delivery: 'zmdi-time',
      other: 'zmdi-more',
    };
    return map[r] || 'zmdi-label';
  }

  /* ─────────────────────────────────────────────────────────────────────────── */
  /*  Main render                                                                  */
  /* ─────────────────────────────────────────────────────────────────────────── */

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home<i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Return Order</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* Page heading */}
          <div className="text-center p-b-40">
            <div className="return-hero-icon">
              <i className="zmdi zmdi-refresh-alt"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Return an Item</h2>
            <p className="stext-113 cl6" style={{ maxWidth: 500, margin: '0 auto' }}>
              Not happy with your order? We make returns easy. Start below and we'll take care of the rest.
            </p>
          </div>

          {/* Step indicator */}
          {renderStepIndicator()}

          {/* Step content */}
          <div className="return-content">
            {step === 'search' && renderSearch()}
            {step === 'select-items' && renderSelectItems()}
            {step === 'reason' && renderReason()}
            {step === 'review' && renderReview()}
            {step === 'success' && renderSuccess()}
          </div>

        </div>
      </section>

      <Footer />
      <BackToTop />

      {/* ── Styles ── */}
      <style>{`
        /* Hero icon */
        .return-hero-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f4f4f9;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 34px;
          color: #717fe0;
        }

        /* ── Step Indicator ── */
        .return-steps {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 36px;
          flex-wrap: wrap;
          gap: 4px;
        }
        .return-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .return-step__bubble {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 2px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 600;
          color: #aaa;
          background: #fff;
          transition: all 0.3s;
        }
        .return-step--active .return-step__bubble {
          border-color: #717fe0;
          color: #717fe0;
        }
        .return-step--done .return-step__bubble {
          border-color: #28a745;
          background: #28a745;
          color: #fff;
        }
        .return-step__label {
          font-size: 11px;
          color: #aaa;
          font-family: Poppins-Regular, sans-serif;
          white-space: nowrap;
        }
        .return-step--active .return-step__label { color: #717fe0; font-weight: 600; }
        .return-step--done .return-step__label { color: #28a745; }
        .return-step__line {
          width: 60px;
          height: 2px;
          background: #eee;
          margin-top: -20px;
          transition: background 0.3s;
        }
        .return-step__line--done { background: #28a745; }

        /* ── Content wrapper ── */
        .return-content {
          max-width: 760px;
          margin: 0 auto;
        }

        /* ── Card ── */
        .return-card {
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          padding: 32px;
          animation: returnCardIn 0.3s ease;
        }
        .return-card--success {
          text-align: center;
        }
        @keyframes returnCardIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .return-card__header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }
        .return-card__icon {
          font-size: 28px;
          color: #717fe0;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .return-card__title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 0 0 4px;
        }
        .return-card__sub {
          font-size: 13px;
          color: #888;
          margin: 0;
        }

        /* ── Form ── */
        .return-form { }
        .return-form__row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }
        .return-form__field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .return-form__label {
          font-size: 13px;
          font-weight: 500;
          color: #555;
          font-family: Poppins-Medium, sans-serif;
        }
        .return-required { color: #dc3545; margin-left: 2px; }
        .return-input-wrap {
          position: relative;
        }
        .return-input-wrap__icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #aaa;
          font-size: 16px;
        }
        .return-input {
          width: 100%;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 11px 14px 11px 42px;
          font-size: 14px;
          outline: none;
          font-family: Poppins-Regular, sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .return-input:focus {
          border-color: #717fe0;
          box-shadow: 0 0 0 3px rgba(113,127,224,0.1);
        }
        .return-textarea {
          width: 100%;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 12px 14px;
          font-size: 14px;
          outline: none;
          font-family: Poppins-Regular, sans-serif;
          resize: vertical;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .return-textarea:focus {
          border-color: #717fe0;
          box-shadow: 0 0 0 3px rgba(113,127,224,0.1);
        }
        .return-hint {
          font-size: 11px;
          color: #999;
          margin: 4px 0 0;
          font-family: Poppins-Regular, sans-serif;
        }

        /* ── Alert ── */
        .return-alert {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-family: Poppins-Regular, sans-serif;
          margin-bottom: 16px;
        }
        .return-alert--error {
          background: #fff5f5;
          border: 1px solid #fcc;
          color: #c33;
        }
        .return-alert--warning {
          background: #fffbee;
          border: 1px solid #ffe58f;
          color: #856404;
        }
        .return-alert i { flex-shrink: 0; margin-top: 1px; }

        /* ── Policy note ── */
        .return-policy-note {
          font-size: 12px;
          color: #888;
          font-family: Poppins-Regular, sans-serif;
          margin-top: 16px;
          padding: 10px 14px;
          background: #f8f9ff;
          border-radius: 6px;
          border-left: 3px solid #717fe0;
        }

        /* ── Buttons ── */
        .return-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 11px 24px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          font-family: Poppins-Medium, sans-serif;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
          text-decoration: none;
          white-space: nowrap;
        }
        .return-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .return-btn--primary {
          background: #717fe0;
          color: #fff;
        }
        .return-btn--primary:hover:not(:disabled) { background: #5a6fd6; }
        .return-btn--success {
          background: #28a745;
          color: #fff;
        }
        .return-btn--success:hover:not(:disabled) { background: #218838; }
        .return-btn--ghost {
          background: #fff;
          color: #555;
          border: 1px solid #ddd;
        }
        .return-btn--ghost:hover:not(:disabled) {
          border-color: #717fe0;
          color: #717fe0;
        }
        .return-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 28px;
          gap: 12px;
          flex-wrap: wrap;
        }
        .return-actions--center { justify-content: center; }
        @keyframes returnSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .return-spin { display: inline-block; animation: returnSpin 0.8s linear infinite; }

        /* ── Order badge ── */
        .return-order-badge {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 16px;
          background: #f8f9ff;
          border-radius: 6px;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 6px;
        }

        /* ── Items list ── */
        .return-items-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 20px;
        }
        .return-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 16px;
          border: 2px solid #eee;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .return-item:hover { border-color: #c8cef5; background: #f8f9ff; }
        .return-item--selected { border-color: #717fe0; background: #f4f5ff; }
        .return-item__check { flex-shrink: 0; }
        .return-checkbox {
          width: 22px;
          height: 22px;
          border-radius: 4px;
          border: 2px solid #ccc;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          color: #fff;
          transition: all 0.2s;
        }
        .return-checkbox--checked {
          background: #717fe0;
          border-color: #717fe0;
        }
        .return-item__img-wrap { flex-shrink: 0; }
        .return-item__img {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 4px;
          border: 1px solid #eee;
        }
        .return-item__img-placeholder {
          width: 60px;
          height: 60px;
          background: #f5f5f5;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: #ccc;
          border: 1px solid #eee;
        }
        .return-item__info { flex: 1; min-width: 0; }
        .return-item__name { font-weight: 600; margin: 0 0 2px; }
        .return-item__variant, .return-item__sku, .return-item__price { margin: 0 0 2px; }

        /* ── Item discount price display ── */
        .return-item__price-wrap {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin: 2px 0;
        }
        .return-item__price-discounted {
          font-weight: 600;
        }
        .return-item__price-original {
          text-decoration: line-through;
          color: #aaa;
          font-size: 12px;
        }
        .return-item__discount-badge {
          background: #e74c3c;
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          padding: 1px 5px;
          border-radius: 3px;
        }

        .return-item__qty-wrap { flex-shrink: 0; }
        .return-qty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .return-qty__label {
          font-size: 10px;
          color: #888;
          font-family: Poppins-Regular, sans-serif;
        }
        .return-qty__controls {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 2px 6px;
        }
        .return-qty__btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          color: #717fe0;
          padding: 0 4px;
          line-height: 1;
          font-family: monospace;
        }
        .return-qty__val {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          min-width: 24px;
          text-align: center;
        }
        .return-qty__max { font-size: 11px; }
        .return-item__total { flex-shrink: 0; }
        .return-item__total-val { font-weight: 600; }

        /* ── Refund preview ── */
        .return-refund-preview {
          padding: 12px 16px;
          background: #f0fff4;
          border: 1px solid #c3e6cb;
          border-radius: 6px;
          font-size: 13px;
          font-family: Poppins-Regular, sans-serif;
          color: #155724;
          margin-bottom: 16px;
        }

        /* ── Reason grid ── */
        .return-reason-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
          gap: 10px;
          margin-bottom: 8px;
        }
        .return-reason-chip {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border: 2px solid #e8e8e8;
          border-radius: 8px;
          background: #fff;
          cursor: pointer;
          text-align: left;
          font-size: 13px;
          font-family: Poppins-Regular, sans-serif;
          color: #555;
          transition: all 0.2s;
        }
        .return-reason-chip:hover { border-color: #c8cef5; background: #f8f9ff; }
        .return-reason-chip--active {
          border-color: #717fe0;
          background: #f0f2ff;
          color: #4a55c8;
          font-weight: 600;
        }
        .return-reason-chip__icon {
          font-size: 18px;
          color: #aaa;
          flex-shrink: 0;
          transition: color 0.2s;
        }
        .return-reason-chip--active .return-reason-chip__icon { color: #717fe0; }

        /* ── Review ── */
        .return-review-section {
          border: 1px solid #f0f0f0;
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 16px;
        }
        .return-review-section__title {
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin: 0 0 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #f0f0f0;
        }
        .return-review-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 6px 0;
          border-bottom: 1px solid #fafafa;
          gap: 16px;
        }
        .return-review-row--col { flex-direction: column; gap: 4px; }
        .return-review-row:last-child { border-bottom: none; }
        .return-review-row__label { flex-shrink: 0; }
        .return-review-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 8px 0;
          border-bottom: 1px solid #fafafa;
        }
        .return-review-item:last-child { border-bottom: none; }
        .return-review-item__info { flex: 1; }
        .return-review-item__prices {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          flex-shrink: 0;
        }
        .return-review-item__original {
          text-decoration: line-through;
          color: #aaa;
          font-size: 11px;
        }

        /* ── Refund summary ── */
        .return-refund-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 16px;
          background: #f0fff4;
          border: 1px solid #c3e6cb;
          border-radius: 6px;
          margin-top: 16px;
        }
        .return-refund-summary__amount {
          font-size: 20px;
          font-weight: 700;
          color: #28a745;
          font-family: Poppins-Bold, sans-serif;
        }

        /* ── Success ── */
        .return-success-icon {
          font-size: 72px;
          margin-bottom: 16px;
          line-height: 1;
        }
        .return-success-details {
          background: #f8f9ff;
          border: 1px solid #e0e4f8;
          border-radius: 6px;
          padding: 16px;
          margin: 20px auto;
          max-width: 440px;
          text-align: left;
        }

        /* ── Responsive ── */
        @media (max-width: 680px) {
          .return-card { padding: 20px 16px; }
          .return-form__row { grid-template-columns: 1fr; }
          .return-step__line { width: 30px; }
          .return-item { flex-wrap: wrap; }
          .return-reason-grid { grid-template-columns: 1fr 1fr; }
          .return-actions { flex-direction: column; }
          .return-btn { width: 100%; }
        }
        @media (max-width: 400px) {
          .return-reason-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default OrderReturn;